import { FC, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { DsmButton, DsmGrid, DsmIcon } from '@dsm-dcs/design-system-react';
import Typography from '@material-ui/core/Typography';
import { Spacer } from '../../../helpers/Spacer';
import { processAndStageStylesV2 } from '../../../../../../_metronic/layout';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { CSSClassesList } from '../../../../helpers/helperTypes';
import { FormType } from '../../common';
import ShrimpPackagingCategoryComponent from './ShrimpPackagingCategoryComponent';
import { OutputType } from '../../../../../../graphql/generated/blonk/shrimp';
import { ShrimpProcessingOutput } from '../../../../models/Baseline/ShrimpBaseline';

interface ShrimpPackagingFormProps {
    formType: FormType;
    fieldItemPrefix: string;
    baseline: ShrimpProcessingOutput | undefined;
}

const ShrimpPackagingForm: FC<ShrimpPackagingFormProps> = ({
    formType,
    fieldItemPrefix,
    baseline
}) => {
    const intl = useIntl();
    const classes = processAndStageStylesV2() as CSSClassesList;
    const { control } = useFormContext();

    const {
        fields: freshMeatAndEdibleOffal,
        append: appendEdible,
        remove: removeEdible,
    } = useFieldArray({
        name: `${fieldItemPrefix}.freshMeatAndEdibleOffal`,
        control,
        keyName: "keyId",
    });

    const {
        fields: cat3SlaughterByProducts,
        append: appendCat3,
        remove: removeCat3,
    } = useFieldArray({
        name: `${fieldItemPrefix}.cat3SlaughterByProducts`,
        control,
        keyName: "keyId",
    });

    const {
        fields: cat1And2MaterialAndWaste,
        append: appendCat1and2,
        remove: removeCat1and2,
    } = useFieldArray({
        name: `${fieldItemPrefix}.cat1And2MaterialAndWaste`,
        control,
        keyName: "keyId",
    });

    const outputMap: Partial<Record<OutputType, any[]>> = {
        [OutputType.FreshMeatAndEdibleOffal]: baseline?.freshMeatAndEdibleOffal,
        [OutputType.Cat_3SlaughterByProducts]: baseline?.cat3SlaughterByProducts,
        [OutputType.Cat_1And_2MaterialAndWaste]: baseline?.cat1And2MaterialAndWaste,
    };

    const packagingTypes = [
        {
            outputType: OutputType.FreshMeatAndEdibleOffal,
            prefix: `${fieldItemPrefix}.freshMeatAndEdibleOffal`,
            labelId: 'BASELINE.FORM.SHRIMP.PROCESSING.PACKAGING.EDIBLE',
            fields: freshMeatAndEdibleOffal,
            append: appendEdible,
            remove: removeEdible,
        },
        {
            outputType: OutputType.Cat_3SlaughterByProducts,
            prefix: `${fieldItemPrefix}.cat3SlaughterByProducts`,
            labelId: 'BASELINE.FORM.SHRIMP.PROCESSING.PACKAGING.CATEGORY_THREE',
            fields: cat3SlaughterByProducts,
            append: appendCat3,
            remove: removeCat3,
        },
        {
            outputType: OutputType.Cat_1And_2MaterialAndWaste,
            labelId: 'BASELINE.FORM.SHRIMP.PROCESSING.PACKAGING.CATEGORY_ONE_AND_A_HALF',
            prefix: `${fieldItemPrefix}.cat1And2MaterialAndWaste`,
            fields: cat1And2MaterialAndWaste,
            append: appendCat1and2,
            remove: removeCat1and2,
        },
    ].filter((p) => (outputMap[p.outputType]?.length ?? 0) > 0);

    const handleAdd = (type: OutputType) => {
        const newItem = {
            id: uuidv4(),
            outputId: '',
            primary: [],
            secondary: [],
            tertiary: [],
        };

        switch (type) {
            case "FRESH_MEAT_AND_EDIBLE_OFFAL": {
                appendEdible(newItem);
                break;
            }
            case "CAT_3_SLAUGHTER_BY_PRODUCTS": {
                appendCat3(newItem);
                break;
            }
            case "CAT_1_AND_2_MATERIAL_AND_WASTE": {
                appendCat1and2(newItem);
                break;
            }
        }
    };

    const handleRemove = (index: number, type: OutputType) => {
        switch (type) {
            case "FRESH_MEAT_AND_EDIBLE_OFFAL": {
                removeEdible(index);
                break;
            }
            case "CAT_3_SLAUGHTER_BY_PRODUCTS": {
                removeCat3(index);
                break;
            }
            case "CAT_1_AND_2_MATERIAL_AND_WASTE": {
                removeCat1and2(index);
                break;
            }
        }
    };

    useEffect(() => {
        if (freshMeatAndEdibleOffal.length === 0) {
            handleAdd(OutputType.FreshMeatAndEdibleOffal);
        }
    }, [freshMeatAndEdibleOffal.length]);

    return (
        <>
            {packagingTypes.map((typeObj, idx) => (
                <div key={typeObj.outputType}>
                    <Typography
                        variant="body1"
                        style={{ fontSize: '20px', fontWeight: 500, color: 'black' }}
                    >
                        {intl.formatMessage({ id: typeObj.labelId })}
                    </Typography>

                    <Spacer height="16px" />

                    {typeObj.fields
                        .map((field, index) => (
                            <div key={field.keyId}>
                                <DsmGrid className={classes.dsmGridOneColumnWithBorder} style={{ padding: '1rem' }}>
                                    {formType !== FormType.View &&
                                        (typeObj.fields.length > 1 ||
                                            typeObj.outputType !== OutputType.FreshMeatAndEdibleOffal) && (
                                            <DsmButton
                                                variant="text"
                                                style={{ position: "relative", width: "100%" }}
                                                onClick={() => handleRemove(index, typeObj.outputType)}
                                            >
                                                <DsmIcon
                                                    name="general/x-close"
                                                    style={{
                                                        position: "absolute",
                                                        color: "var(--dsm-color-neutral-darker)",
                                                        right: "0",
                                                    }}
                                                />
                                            </DsmButton>
                                        )}

                                    <ShrimpPackagingCategoryComponent
                                        formType={formType}
                                        fieldItemPrefix={`${typeObj.prefix}.${index}`}
                                        outputOptions={
                                            (outputMap[typeObj.outputType] || []).map((o: any) => ({
                                                value: o.outputId,
                                                text: o.outputName,
                                            }))
                                        }
                                    />
                                </DsmGrid>

                                <Spacer height="16px" />
                            </div>
                        ))}

                    {formType !== FormType.View && (
                        <DsmButton
                            variant="text"
                            onClick={() => handleAdd(typeObj.outputType)}
                            disabled={typeObj.fields.length >= (outputMap[typeObj.outputType]?.length ?? 0)}
                        >
                            <DsmIcon
                                slot="before"
                                name="general/plus-circle"
                            />
                            {intl.formatMessage({
                                id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.ADD_PACKAGING_ANOTHER_OUTPUT',
                            })}
                        </DsmButton>
                    )}

                    {idx < packagingTypes.length - 1 && <Spacer height="24px" />}
                </div>
            ))}
        </>
    );
};

export default ShrimpPackagingForm;
